import type { Entry } from "contentful";
import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { z } from "zod";

import { withZod } from "@rvf/zod";

import { Form } from "~/components/forms/Form";
import type { Store } from "~/components/types";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { LocationIcon } from "~/components/ui/location-icon";
import { PageHeader } from "~/components/ui/page-header";
import type { TypeImageLinkSkeleton } from "~/contentful/compiled";
import { cn } from "~/lib/ui";

import { useStoreActions } from "../resources/store-actions";
import StoresList from "./stores-list";

const storeLocatorSchema = z.object({
  address: z.string().min(1),
  radius: z.string().min(1),
});

const radiusOptions = [
  { value: "15", label: "15 km" },
  { value: "30", label: "30 km" },
  { value: "50", label: "50 km" },
  { value: "100", label: "100 km" },
  { value: "300", label: "300 km" },
];

const storeLocatorValidator = withZod(storeLocatorSchema);

const RightSideContent = ({
  shops,
  mapCenter,
  setMapCenter,
  total,
  brandLogo,
  setHoveredStoreId,
  customerStore,
}: {
  shops: Store[];
  mapCenter: {
    lat: number;
    lng: number;
  };
  total: number;
  setMapCenter: (data: { lat: number; lng: number }) => void;
  brandLogo: Entry<TypeImageLinkSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>;
  setHoveredStoreId: (id: string | null) => void;
  customerStore?: string;
}) => {
  const [searchErrorMessage, setSearchErrorMessage] = useState("");
  const [userLocationSearchErrorMessage, setUserLocationSearchErrorMessage] =
    useState("");
  const [selectedStore, setSelectedStore] = useState(customerStore ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const coordinates = {
    lat: mapCenter.lat,
    lng: mapCenter.lng,
  };
  const { addStore } = useStoreActions();

  useEffect(() => {
    if (!searchParams.size) {
      handleUserLocationClick();
    } else {
      setMapCenter({
        lat: parseFloat(searchParams.get("lat")?.toString() || ""),
        lng: parseFloat(searchParams.get("lng")?.toString() || ""),
      });
    }
  }, [location.key]);

  const handleAutocompleteLoad = () => {
    const { maps } = (window as any).google;
    const autocomplete = new maps.places.Autocomplete(inputRef.current);

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      const address = place.formatted_address;
      if (address && inputRef.current) {
        inputRef.current.value = address;
        handleSearchClick({ address });
      }
    });
  };

  const getAllStores = async (lat: number, lng: number, radius: number) => {
    setSearchParams(prev => {
      prev.set("lat", encodeURIComponent(lat));
      prev.set("lng", encodeURIComponent(lng));
      prev.set("radius", encodeURIComponent(radius));
      prev.set("page", encodeURIComponent(1));

      return prev;
    });
    lat = parseFloat(lat.toString());
    lng = parseFloat(lng.toString());
    setMapCenter({ lat, lng });
  };

  const handleSearchClick = ({ address }: { address: string }) => {
    const { maps } = (window as any).google;
    const geocoder = new maps.Geocoder();
    geocoder.geocode({ address }, (results: any, status: string) => {
      setUserLocationSearchErrorMessage("");
      if (status === "OK") {
        const userLocation = results[0].geometry.location;
        getAllStores(
          userLocation.lat(),
          userLocation.lng(),
          +radiusOptions[0].value,
        );
      } else {
        setSearchErrorMessage(
          `We're sorry, we couldn't find results for your search.`,
        );
      }
    });
  };

  const handleUserLocationClick = () => {
    if (navigator.geolocation) {
      setUserLocationSearchErrorMessage("User location is not available.");
      navigator.permissions.query({ name: "geolocation" }).then(result => {
        if (result.state === "denied") {
          return alert(
            "Location access is denied. Please enable it in your browser settings.",
          );
        }
      });

      navigator.geolocation.getCurrentPosition(position => {
        setUserLocationSearchErrorMessage("");
        setSearchErrorMessage("");
        getAllStores(
          position.coords.latitude,
          position.coords.longitude,
          +radiusOptions[0].value,
        );
      });
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    } else {
      setUserLocationSearchErrorMessage(
        "Geolocation is not supported by this browser.",
      );
    }
  };

  const handleSetMyStore = async () => {
    if (selectedStore) {
      try {
        setIsSubmitting(true);
        addStore(selectedStore);
      } catch (error) {
        console.error("Failed to set store preference:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="relative w-full px-4 md:w-1/4" ref={listRef}>
      <Form validator={storeLocatorValidator}>
        <div className="contents">
          <div className="px-2">
            <PageHeader
              heading="Store Finder"
              variant="blogPost"
              className="mx-auto justify-center py-2"
            />
            <Input
              type="text"
              name="address"
              ref={inputRef}
              placeholder="Please enter a location"
              onFocus={handleAutocompleteLoad}
            />
            {searchErrorMessage && <p>{searchErrorMessage}</p>}
            <div className="pt-4 text-center">
              <Button
                variant="outline"
                className="w-full cursor-pointer py-2"
                onClick={() => handleUserLocationClick()}
              >
                <LocationIcon width="18" height="18" className="mb-1" />
                <span className="px-1">Use My Location</span>
              </Button>
              {userLocationSearchErrorMessage && (
                <p className="mt-5">{userLocationSearchErrorMessage}</p>
              )}
            </div>
          </div>
          <StoresList
            shops={shops}
            mapCenter={mapCenter}
            brandLogo={brandLogo}
            setHoveredStoreId={setHoveredStoreId}
            setSelectedStore={setSelectedStore}
            selectedStore={selectedStore}
            total={total}
            coordinates={coordinates}
          />
          <div
            className={cn(
              "sticky bottom-0 left-0 w-full border-t bg-background p-4",
              (!shops || shops.length < 1) && "hidden",
            )}
          >
            <Button
              className="w-full"
              disabled={
                !selectedStore ||
                isSubmitting ||
                selectedStore === customerStore
              }
              onClick={handleSetMyStore}
            >
              {isSubmitting
                ? "Setting..."
                : selectedStore === customerStore
                  ? "My Store"
                  : "Set as My Store"}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default RightSideContent;
